import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import swal from 'sweetalert2';
import { SurveyService } from './survey.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CustomSharedModule } from '../../../custom/shared/custom.shared.module';

@Component({
  selector: 'app-survey',
  templateUrl: './survey.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, CustomSharedModule], // Import needed modules if standalone = true
  styleUrls: ['./survey.component.scss']
})
export class SurveyComponent implements OnInit, OnDestroy {

  errorMessageList: string[] = [];

  showSurvey = false;
  isSubmitted = false;
  currentIndex = 0;

  public survey: any = {
    questions: []
  };

  ratingOptions = [
    { value: 5, image: '/assets/icons/icon-face-rating-5.png', label: 'Excellent' },
    { value: 4, image: '/assets/icons/icon-face-rating-4.png', label: 'Very Good' },
    { value: 3, image: '/assets/icons/icon-face-rating-3.png', label: 'Good' },
    { value: 2, image: '/assets/icons/icon-face-rating-2.png', label: 'Fair' },
    { value: 1, image: '/assets/icons/icon-face-rating-1.png', label: 'Poor' },
  ];

  direction: 'prev' | 'next' = 'next';

  @ViewChild('nextBtn') nextBtn!: ElementRef<HTMLButtonElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private surveyService: SurveyService,
  ) { }

  ngOnInit(): void {

    document.body.classList.add('bg-primary');

    // Sort ratings
    this.ratingOptions.sort((a, b) => b.value - a.value);

    // Subscribe to query string changes
    this.route.queryParams.subscribe(params => {

      const access = params['id'];

      if (access) {
        this.getOnBoardSurveyByShipAccess(access);
      }
      else {
        swal.fire({
          title: '',
          html: '<span>On board survey access is required</span>',
          icon: "warning",
          showCloseButton: true,
          allowOutsideClick: false,
          allowEscapeKey: false
        })
          .then((result) => {
            if (result) {
              this.router.navigate(['/admin/home']);
            }
          });
      }
    });

  }

  ngOnDestroy() {
    document.body.classList.remove('bg-primary');
  }

  startSurvey(): void {
    this.showSurvey = true;
    setTimeout(() => this.nextBtn?.nativeElement.focus());
  }

  @HostListener('document:keydown.enter')
  handleEnterKey(): void {
    if (!this.showSurvey) {
      this.startSurvey();
    }
  }

  selectRating(model: any, value: number, btn: HTMLElement) {

    model.answerRating = value;

    // Auto move to next after 1 second
    if (this.survey.questions.length > 1) {

      btn.classList.add('selected-animation');

      setTimeout(() => {
        this.next(false);
      }, 1000);
    }
  }

  selectOption(model: any, value: number, btn: HTMLElement) {

    model.answerOptionID = value;

    // Auto move to next after 1 second
    if (this.survey.questions.length > 1) {

      btn.classList.add('selected-animation');

      setTimeout(() => {
        this.next(false);
      }, 1000);
    }
  }

  get currentQuestion(): any {
    return this.survey.questions[this.currentIndex];
  }

  get progressPercent(): number {
    return ((this.currentIndex + 1) / this.survey.questions.length) * 100;
  }

  next(validate: boolean = true): void {

    this.errorMessageList = [];

    if (validate && this.validate() || !validate) {
      if (this.currentIndex < this.survey.questions.length - 1) {
        this.direction = 'next';
        this.currentIndex++;
      }
    }
  }

  prev(): void {

    this.errorMessageList = [];

    if (this.currentIndex > 0) {
      this.direction = 'prev';
      this.currentIndex--;
    }
    else {
      // reset
      this.currentIndex = 0;
      this.showSurvey = false;
    }
  }

  validate() {
    this.errorMessageList = [];

    const currentQuestion = this.survey.questions[this.currentIndex];

    if (currentQuestion.questionType === 'Rating') {

      if (!currentQuestion.answerRating || Number(currentQuestion.answerRating) == 0) {
        this.errorMessageList.push("Oops! Please make a selection");
      }

    }

    return (this.errorMessageList.length === 0)
  }

  submit(): void {

    if (this.validate()) {

      this.surveyService.submitOnBoardSurvey(this.survey).subscribe({
        next: (res: any) => {
          if (this.isResponseValid(res)) {

            this.isSubmitted = true;

            // reset
            this.currentIndex = 0;
            this.showSurvey = false;
          }
        },
        error: (err: any) => this.handleError(err)
      });

    }
  }

  reload() {
    // reload page
    window.location.reload();
  }

  private getOnBoardSurveyByShipAccess(access: string) {

    this.surveyService.getOnBoardSurveyByShipAccess(access).subscribe({
      next: (res: any) => {
        if (this.isResponseValid(res)) {

          if (res.entity.list.length > 0) {
            this.survey = res.entity.list[0];
            this.survey.onBoardSurveyAccess = access;
            this.survey.questions.sort((a: any, b: any) =>
              ((a.sequence ?? 0) - (b.sequence ?? 0)) ||
              (a.onBoardSurveyQuestion.localeCompare(b.onBoardSurveyQuestion))
            );
          }
        }

      },
      error: (err: any) => this.handleError(err)
    });
  }

  private handleError(error: any) {
    let errorTitle = error.name ? error.name : 'Error';
    let errorMessage = error.message ? error.message : JSON.stringify(error);

    swal.fire({
      title: errorTitle,
      html: '<span>' + errorMessage + '</span>',
      icon: "error",
      showCloseButton: true,
      allowOutsideClick: false,
      allowEscapeKey: false
    })
      .then((result) => {
        if (result) {
          this.router.navigate(['/admin/home']);
        }
      });
  }

  private isResponseValid(res: any): boolean {
    let isValid = true;

    if (res.errorMessage.length > 0) {
      isValid = false;
      this.handleError(res.errorMessage[0]);
    }

    return isValid;
  }

}
